import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import crossIcon from "../assets/icons/crossIcon.png";
import editIcon from "../assets/Images/Edit.png";
import deleteIcon from "../assets/icons/deleteIcon.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import * as XLSX from "xlsx";
import viewIcon from "../assets/Images/View.png";
import settingIcon from "../assets/Images/Settings.png";
import { saveAs } from "file-saver";
import departmentIcon from "../assets/icons/department.svg";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import enterpriseIcon from "../assets/icons/enterprisesvg.png";
import usersIcon from "../assets/icons/users.png";
import modelsIcon from "../assets/icons/productsvg.png";
import conversationIcon from "../assets/icons/conversation.png";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { useDispatch } from "react-redux";
import { setSelectedMenu } from "../redux/sidebarSlice";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const BASE_URL = `${process.env.REACT_APP_API_URL}/enterprises`;

const Enterprize = () => {
  const [enterprises, setEnterprises] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [dayFilter, setDayFilter] = useState("");
  const [isViewing, setIsViewing] = useState(false);
  const [userCounts, setUserCounts] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  });
  const [enterpriseCounts, setEnterpriseCounts] = useState({
    totalEnterprises: 0,
    activeEnterprises: 0,
    inactiveEnterprises: 0,
  });
  const [documentStats, setDocumentStats] = useState({
    totalDocuments: 0,
    approvedDocuments: 0,
    pendingDocuments: 0,
    rejectedDocuments: 0,
    totalSizeBytes: 0,
    totalSizeMB: "0.00",
  });

  const [departmentCounts, setDepartmentCounts] = useState({
    totalDepartments: 0,
    activeDepartments: 0,
    inactiveDepartments: 0,
  });

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  const [modelStats, setModelStats] = useState({
    active: 0,
    in_review: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [chatStats, setChatStats] = useState([]);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handleReset = () => {
    setSortOrder("");
    setDayFilter("");
  };

  const [formData, setFormData] = useState({
    enterprise_name: "",
    enterprise_address_1: "",
    enterprise_address_2: "",
    enterprise_city: "",
    enterprise_state: "",
    enterprise_zip_code: "",
    enterprise_country: "",
    enterprise_phone_1: "",
    enterprise_phone_2: "",
    enterprise_mobile_1: "",
    enterprise_mobile_2: "",
    enterprise_email_1: "",
    enterprise_email_2: "",
    enterprise_logo: null,
    enterprise_theme: "",
    contact_person_name: "",
    contact_person_phone: "",
    contact_person_email: "",
    contact_person_mobile: "",
    contact_person_designation: "",
    alt_contact_person_name: "",
    alt_contact_person_phone: "",
    alt_contact_person_email: "",
    alt_contact_person_mobile: "",
    alt_contact_person_designation: "",
  });

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const authHeader = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchEnterprises = async () => {
    const safeGet = async (url) => {
      try {
        const res = await api.get(url, authHeader);
        return { ok: true, data: res.data };
      } catch (err) {
        console.warn("API failed:", {
          url,
          status: err.response?.status,
          message: err.message,
        });
        return { ok: false };
      }
    };

    try {
      /* =========================
       🔴 CRITICAL API
    ========================== */
      const enterpriseRes = await safeGet(
        `${process.env.REACT_APP_API_URL}/enterprises/all`,
      );

      if (!enterpriseRes.ok) {
        showErrorToast("Unable to load enterprises. Please try again.");
        return; // ⛔ stop page render
      }

      setEnterprises(enterpriseRes.data);

      /* =========================
       🟡 OPTIONAL APIs (parallel)
    ========================== */
      const results = await Promise.allSettled([
        safeGet(`${process.env.REACT_APP_API_URL}/users/count/all`),
        safeGet(`${process.env.REACT_APP_API_URL}/enterprises/counts/all`),
        safeGet(`${process.env.REACT_APP_API_URL}/departments/counts/all`),
        safeGet(`${process.env.REACT_APP_API_URL}/chat/summary`),
        safeGet(
          `${process.env.REACT_APP_API_URL}/uploaded-files/stats/summary`,
        ),
        safeGet(`${process.env.REACT_APP_API_URL}/models1/status-summary`),
      ]);

      const [
        userCountRes,
        enterpriseCountRes,
        departmentCountRes,
        chatSummaryRes,
        documentStatsRes,
        modelRes,
      ] = results.map((r) => r.value);

      /* =========================
       ✅ SAFE STATE SETTERS
    ========================== */

      if (userCountRes?.ok) {
        setUserCounts(userCountRes.data);
      }

      if (enterpriseCountRes?.ok) {
        setEnterpriseCounts({
          totalEnterprises: enterpriseCountRes.data.enterprise?.total || 0,
          activeEnterprises: enterpriseCountRes.data.enterprise?.active || 0,
          inactiveEnterprises:
            enterpriseCountRes.data.enterprise?.inactive || 0,
        });
      }

      if (departmentCountRes?.ok) {
        setDepartmentCounts({
          totalDepartments: departmentCountRes.data.total || 0,
          activeDepartments: departmentCountRes.data.active || 0,
          inactiveDepartments: departmentCountRes.data.inactive || 0,
        });
      }

      if (chatSummaryRes?.ok) {
        setChatStats(chatSummaryRes.data);
      }

      if (documentStatsRes?.ok) {
        setDocumentStats(documentStatsRes.data);
      }

      if (modelRes?.ok) {
        setModelStats(modelRes.data);
      }
    } catch (err) {
      // 🔥 This should almost never happen now
      console.error("Unexpected fetchEnterprises crash:", err);
      showErrorToast("Something went wrong. Please refresh.");
    }
  };

  useEffect(() => {
    fetchEnterprises();
  }, []);

  const enrichedEnterprises = enterprises.map((row) => {
    const chatStatsForEnterprise = chatStats.find(
      (stat) => String(stat.enterpriseId) === String(row.enterprise_id),
    );

    return {
      ...row,
      totalUsers: chatStatsForEnterprise?.totalUsersInEnterprise ?? 0,
      totalConversations: chatStatsForEnterprise?.totalConversations ?? 0,
    };
  });

  // Filter, Search, Sort Logic
  const filteredData = enrichedEnterprises
    .filter((ent) => {
      const term = searchTerm.toLowerCase();
      return (
        ent.enterprise_name?.toLowerCase().includes(term) ||
        ent.enterprise_city?.toLowerCase().includes(term) ||
        ent.enterprise_state?.toLowerCase().includes(term) ||
        ent.enterprise_country?.toLowerCase().includes(term) ||
        ent.enterprise_mobile_1?.toLowerCase().includes(term)
      );
    })
    .filter((ent) => {
      if (dayFilter === "Today") {
        const createdDate = new Date(ent.created_at);
        const today = new Date();
        return createdDate.toDateString() === today.toDateString();
      }
      if (dayFilter === "Yesterday") {
        const createdDate = new Date(ent.created_at);
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return createdDate.toDateString() === yesterday.toDateString();
      }
      return true;
    })
    .sort((a, b) => {
      // Default sort: by updated_timestamp descending
      return new Date(b.updated_timestamp) - new Date(a.updated_timestamp);
    });

  const totalEntries = filteredData.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredData.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = indexOfLast > totalEntries ? totalEntries : indexOfLast;
  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      enterprise_name: "",
      enterprise_address_1: "",
      enterprise_address_2: "",
      enterprise_city: "",
      enterprise_state: "",
      enterprise_zip_code: "",
      enterprise_country: "",
      enterprise_phone_1: "",
      enterprise_phone_2: "",
      enterprise_mobile_1: "",
      enterprise_mobile_2: "",
      enterprise_email_1: "",
      enterprise_email_2: "",
      enterprise_logo: null,
      enterprise_theme: "",
      contact_person_name: "",
      contact_person_phone: "",
      contact_person_email: "",
      contact_person_mobile: "",
      contact_person_designation: "",
      alt_contact_person_name: "",
      alt_contact_person_phone: "",
      alt_contact_person_email: "",
      alt_contact_person_mobile: "",
      alt_contact_person_designation: "",
    });
    setShowModal(true);
  };

  const handleEditClick = (ent) => {
    setIsEditing(true);
    setEditingId(ent.enterprise_id);

    setFormData({
      enterprise_name: ent.enterprise_name || "",
      enterprise_address_1: ent.enterprise_address_1 || "",
      enterprise_address_2: ent.enterprise_address_2 || "",
      enterprise_city: ent.enterprise_city || "",
      enterprise_state: ent.enterprise_state || "",
      enterprise_zip_code: ent.enterprise_zip_code || "",
      enterprise_country: ent.enterprise_country || "",
      enterprise_phone_1: ent.enterprise_phone_1 || "",
      enterprise_phone_2: ent.enterprise_phone_2 || "",
      enterprise_mobile_1: ent.enterprise_mobile_1 || "",
      enterprise_mobile_2: ent.enterprise_mobile_2 || "",
      enterprise_email_1: ent.enterprise_email_1 || "",
      enterprise_email_2: ent.enterprise_email_2 || "",
      enterprise_logo: null, // File input will override this
      enterprise_theme: ent.enterprise_theme || "",
      contact_person_name: ent.contact_person_name || "",
      contact_person_phone: ent.contact_person_phone || "",
      contact_person_email: ent.contact_person_email || "",
      contact_person_mobile: ent.contact_person_mobile || "",
      contact_person_designation: ent.contact_person_designation || "",
      alt_contact_person_name: ent.alt_contact_person_name || "",
      alt_contact_person_phone: ent.alt_contact_person_phone || "",
      alt_contact_person_email: ent.alt_contact_person_email || "",
      alt_contact_person_mobile: ent.alt_contact_person_mobile || "",
      alt_contact_person_designation: ent.alt_contact_person_designation || "",
    });

    // 👇 Set preview if enterprise_logo exists
    if (ent.enterprise_logo) {
      setPreviewUrl(
        `${process.env.REACT_APP_SERVER_URL}/uploads/${ent.enterprise_logo}`,
      );
    } else {
      setPreviewUrl(""); // Clear preview if none exists
    }

    setShowModal(true);
  };

  const handleViewClick = (ent) => {
    setIsViewing(true);
    setIsEditing(false);
    setEditingId(ent.enterprise_id);

    setFormData({
      enterprise_name: ent.enterprise_name || "",
      enterprise_address_1: ent.enterprise_address_1 || "",
      enterprise_address_2: ent.enterprise_address_2 || "",
      enterprise_city: ent.enterprise_city || "",
      enterprise_state: ent.enterprise_state || "",
      enterprise_zip_code: ent.enterprise_zip_code || "",
      enterprise_country: ent.enterprise_country || "",
      enterprise_phone_1: ent.enterprise_phone_1 || "",
      enterprise_phone_2: ent.enterprise_phone_2 || "",
      enterprise_mobile_1: ent.enterprise_mobile_1 || "",
      enterprise_mobile_2: ent.enterprise_mobile_2 || "",
      enterprise_email_1: ent.enterprise_email_1 || "",
      enterprise_email_2: ent.enterprise_email_2 || "",
      enterprise_logo: null,
      enterprise_theme: ent.enterprise_theme || "",
      contact_person_name: ent.contact_person_name || "",
      contact_person_phone: ent.contact_person_phone || "",
      contact_person_email: ent.contact_person_email || "",
      contact_person_mobile: ent.contact_person_mobile || "",
      contact_person_designation: ent.contact_person_designation || "",
      alt_contact_person_name: ent.alt_contact_person_name || "",
      alt_contact_person_phone: ent.alt_contact_person_phone || "",
      alt_contact_person_email: ent.alt_contact_person_email || "",
      alt_contact_person_mobile: ent.alt_contact_person_mobile || "",
      alt_contact_person_designation: ent.alt_contact_person_designation || "",
    });

    if (ent.enterprise_logo) {
      setPreviewUrl(
        `${process.env.REACT_APP_SERVER_URL}/uploads/${ent.enterprise_logo}`,
      );
    } else {
      setPreviewUrl("");
    }

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      await fetchEnterprises();
    } catch (err) {
      console.error("handleDelete error:", err);
      alert("Error deleting entry.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "enterprise_logo") {
      setFormData((prev) => ({
        ...prev,
        enterprise_logo: files && files.length > 0 ? files[0] : null,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start Loading

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "enterprise_logo") {
        if (value instanceof File) {
          payload.append(key, value);
        }
      } else {
        payload.append(key, value || "");
      }
    });

    try {
      const url = isEditing ? `${BASE_URL}/${editingId}` : BASE_URL;
      const method = isEditing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: payload,
      });

      if (!res.ok) throw new Error("Save failed");

      showSuccessToast(
        isEditing
          ? TOAST_MESSAGES.ENTERPRISE.UPDATE_SUCCESS
          : TOAST_MESSAGES.ENTERPRISE.CREATE_SUCCESS,
      );

      setShowModal(false);
      fetchEnterprises();
    } catch (err) {
      showErrorToast(
        isEditing
          ? TOAST_MESSAGES.ENTERPRISE.UPDATE_ERROR
          : TOAST_MESSAGES.ENTERPRISE.CREATE_ERROR,
      );
    } finally {
      setIsSubmitting(false); //  Reset Loading State
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsViewing(false);
    setIsEditing(false);
    setPreviewUrl(""); // reset logo preview
    setFormData(formData); // reset all input fields
  };

  const exportExcel = () => {
    const exportData = filteredData.map((ent) => ({
      "Enterprize Name": ent.enterprise_name,
      City: ent.enterprise_city,
      State: ent.enterprise_state,
      Country: ent.enterprise_country,
      "Mobile 1": ent.enterprise_mobile_1,
      // "Total Users": totalUsers ?? 0,
      // "Total Chats": ent.totalChats ?? 0,
      Status: ent.enterprise_status === "1" ? "Active" : "Inactive",
      "Last Updated": ent.updated_timestamp
        ? formatDateToUserTimeZone(ent.updated_timestamp)
        : "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enterprizes");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "Enterprizes.xlsx",
    );
  };

  const handleChangeStatus = async (id, currentStatus) => {
    try {
      setIsChangingStatus(true);

      const newStatus = currentStatus === "1" ? "0" : "1";
      const statusText = newStatus === "1" ? "Active" : "Inactive";

      await api.patch(
        `${BASE_URL}/${id}/status`,
        { enterprise_status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      showSuccessToast(
        `${TOAST_MESSAGES.ENTERPRISE.STATUS_CHANGE_SUCCESS} to ${statusText}`,
      );

      fetchEnterprises();
      setShowStatusModal(false);
      setSelectedEnterprise(null);
    } catch (err) {
      console.error(err);
      showErrorToast(
        err.response?.data?.error ||
          TOAST_MESSAGES.ENTERPRISE.STATUS_CHANGE_ERROR,
      );
    } finally {
      setIsChangingStatus(false);
    }
  };

  const overallChatSummary =
    Array.isArray(chatStats) && chatStats.length > 0
      ? chatStats[chatStats.length - 1]
      : { totalConversations: 0, usedModelCount: 0 };

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {/* Block 1 */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-1">
            <img
              src={enterpriseIcon}
              alt="Enterprize"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0"> Enterprizes</h6>
          </div>
          <h2 className="fw-bold">{enterpriseCounts.totalEnterprises}</h2>
          <div className="box-summary">
            <span>Active: {enterpriseCounts.activeEnterprises}</span>
            <span>Inactive: {enterpriseCounts.inactiveEnterprises}</span>
          </div>
        </div>

        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-1">
            <img
              src={departmentIcon}
              alt="Department"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Group</h6>
          </div>

          <h2 className="fw-bold">{departmentCounts.totalDepartments}</h2>

          <div className="box-summary">
            <span>Active: {departmentCounts.activeDepartments}</span>
            <span>Inactive: {departmentCounts.inactiveDepartments}</span>
          </div>
        </div>

        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between ">
          <div className="d-flex align-items-center mb-1">
            <img
              src={usersIcon}
              alt="Users"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Total Users</h6>
          </div>
          <h2 className="fw-bold">{userCounts.totalUsers}</h2>
          <div className="box-summary">
            <span>Active: {userCounts.activeUsers}</span>
            <span>Inactive: {userCounts.inactiveUsers}</span>
          </div>
        </div>

        {/* Block 3 */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between ">
          <div className="d-flex align-items-center mb-1">
            <img
              src={conversationIcon}
              alt="Conversations"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0"> Conversations</h6>
          </div>
          <h2 className="fw-bold">{overallChatSummary.totalConversations}</h2>
          {/* <div className="box-summary">
      <span>Approved: 70</span>
      <span>Rejected: 18</span>
    </div> */}
        </div>

        {/* Block 4 */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between ">
          <div className="d-flex align-items-center mb-1">
            <img
              src={modelsIcon}
              alt="Models"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Used Models</h6>
          </div>
          <h2 className="fw-bold">{overallChatSummary.usedModelCount}</h2>
          <div className="box-summary">
            <span>Active: {modelStats.active}</span>
            <span>Inactive: {modelStats.in_review}</span>
          </div>
        </div>

        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between ">
          <div className="d-flex align-items-center mb-1">
            <img
              src={modelsIcon}
              alt="Models"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Documents</h6>
          </div>
          <h2 className="fw-bold">{documentStats.totalDocuments}</h2>
          {/* <div className="box-summary">
      <span>Today: 35</span>
      <span>This Week: 210</span>
    </div> */}
        </div>

        {/* <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-1">
            <img
              src={modelsIcon}
              alt="Models"
              style={{
                width: "21px",
                height: "21px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">System Alerts</h6>
          </div>
          <h2 className="fw-bold">0</h2>
         
        </div> */}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1  ">
        <span className="fw-bold refresh-prompts ak-text-lg">
          Enterprize Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                readOnly={isViewing}
                type="text"
                className="form-control"
                placeholder="Search Enterprizes"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export in Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image1" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    readOnly={isViewing}
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder refresh-prompts">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    readOnly={isViewing}
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => setCurrentPage(1)}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark ms-1"
            onClick={openAddModal}
            title="Add Enterprize"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table enterprise-table1 table-sm w-100">
              <thead>
                <tr>
                  <th>Logo</th>
                  <SortableHeader
                    column="enterprise_name"
                    label="Enterprize Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="enterprise_city"
                    label="City"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="enterprise_state"
                    label="States"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="enterprise_country"
                    label="Country"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="enterprise_mobile_1"
                    label="Mobile 1"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="totalUsers"
                    label="Total Users"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className="text-center"
                  />
                  <SortableHeader
                    column="totalConversations"
                    label="Total Chats"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className="text-center"
                  />
                  <SortableHeader
                    column="enterprise_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((row) => (
                  <tr key={row.enterprise_id}>
                    {/* <td>Logo logic</td> */}
                    <td>
                      {row.enterprise_logo ? (
                        <img
                          src={`${process.env.REACT_APP_SERVER_URL}/${row.enterprise_logo}`}
                          alt="Logo"
                          style={{
                            width: "25px",
                            height: "25px",
                            objectFit: "contain",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <div className="circle-avatar">
                          {" "}
                          {row.enterprise_name
                            ? row.enterprise_name.charAt(0)
                            : "?"}{" "}
                        </div>
                      )}
                    </td>
                    <td>{row.enterprise_name}</td>
                    <td>{row.enterprise_city}</td>
                    <td>{row.enterprise_state}</td>
                    <td>{row.enterprise_country}</td>
                    <td>{row.enterprise_mobile_1}</td>
                    <td className="text-center">{row.totalUsers}</td>
                    <td className="text-center">{row.totalConversations}</td>
                    <td>
                      {row.enterprise_status === "1" ? "Active" : "Inactive"}
                    </td>
                    <td>{formatDateToUserTimeZone(row.updated_timestamp)}</td>
                    <td>
                      {" "}
                      <button
                        className="icon-button"
                        onClick={() => handleViewClick(row)}
                        title="View"
                      >
                        {" "}
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="View"
                          style={{ height: "16px", width: "18px" }}
                        />{" "}
                      </button>{" "}
                      <button
                        className="icon-button"
                        onClick={() => handleEditClick(row)}
                        title="Edit"
                      >
                        {" "}
                        <img
                          src={editIcon}
                          className="icon-image"
                          alt="Edit"
                        />{" "}
                      </button>{" "}
                      <button
                        className="icon-button"
                        onClick={() => {
                          setSelectedEnterprise({
                            id: row.enterprise_id,
                            status: row.enterprise_status,
                          });
                          setShowStatusModal(true);
                        }}
                        title="Change Status"
                      >
                        {" "}
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />{" "}
                      </button>{" "}
                      <button
                        className="icon-button"
                        title="Settings"
                        onClick={() =>
                          navigate("/superadmin/enterprize-setting", {
                            state: { enterprise: row },
                          })
                        }
                      >
                        {" "}
                        <img
                          className="icon-image"
                          src={settingIcon}
                          alt="settings"
                        />{" "}
                      </button>{" "}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Modal code exactly as before, with updated field names */}
      {showModal && (
        <div
          className="modal modal-backdrop-custom d-block"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content w-75 m-auto">
              <form onSubmit={handleModalSubmit}>
                <div className="modal-header justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {isViewing
                      ? "View Enterprize Details"
                      : isEditing
                        ? "Edit Enterprize Details"
                        : "Add Enterprize Details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn position-absolute end-0 me-3"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img
                      src={crossIcon}
                      alt="Close"
                      className="cross-icon-img icon-image"
                    />
                  </button>
                </div>

                <div className="modal-body">
                  {/* Enterprize Details */}
                  <div className="mb-4">
                    <div className="section-header d-flex align-items-center mb-3">
                      <div className="section-badge me-2">1</div>
                      <h5 className="section-title mb-0">Enterprize Details</h5>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4 ">
                        <label>
                          Enterprize Name
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_name"
                          className="form-control"
                          value={formData.enterprise_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Address 1</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_address_1"
                          className="form-control"
                          value={formData.enterprise_address_1}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Address 2</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_address_2"
                          className="form-control"
                          value={formData.enterprise_address_2}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>
                          City
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_city"
                          className="form-control"
                          value={formData.enterprise_city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label>
                          State
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_state"
                          className="form-control"
                          value={formData.enterprise_state}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Zip Code</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_zip_code"
                          className="form-control"
                          value={formData.enterprise_zip_code}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>Phone 1</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_phone_1"
                          className="form-control"
                          value={formData.enterprise_phone_1}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Phone 2</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_phone_2"
                          className="form-control"
                          value={formData.enterprise_phone_2}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>
                          Mobile 1
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_mobile_1"
                          className="form-control"
                          value={formData.enterprise_mobile_1}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>Mobile 2</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_mobile_2"
                          className="form-control"
                          value={formData.enterprise_mobile_2}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Email 1</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_email_1"
                          type="email"
                          className="form-control"
                          value={formData.enterprise_email_1}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Email 2</label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_email_2"
                          type="email"
                          className="form-control"
                          value={formData.enterprise_email_2}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>
                          Country
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="enterprise_country"
                          className="form-control"
                          value={formData.enterprise_country}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Enterprize Logo</label>
                        <div className="d-flex align-items-center gap-3">
                          {previewUrl && (
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{
                                width: "24px",
                                height: "24px",
                                objectFit: "cover",
                                borderRadius: "50%",
                                border: "1px solid #ccc",
                              }}
                            />
                          )}
                          <input
                            readOnly={isViewing}
                            type="file"
                            name="enterprise_logo"
                            className="file-input-reverse"
                            disabled={isViewing}
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setFormData((prev) => ({
                                  ...prev,
                                  enterprise_logo: file,
                                }));

                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setPreviewUrl(reader.result);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setPreviewUrl("");
                                setFormData((prev) => ({
                                  ...prev,
                                  enterprise_logo: null,
                                }));
                              }
                            }}
                          />
                        </div>
                      </div>

                      {/* <div className="col-md-4">
                        <label>Theme</label>
                        <input  readOnly={isViewing}
                          name="enterprise_theme"
                          className="form-control"
                          value={formData.enterprise_theme}
                          onChange={handleInputChange}
                        />
                      </div> */}
                    </div>
                  </div>

                  {/* Contact Person */}
                  <div className="mb-3">
                    <div className="section-header d-flex align-items-center mb-3">
                      <div className="section-badge me-2">2</div>
                      <h5 className="section-title mb-0">
                        Primary Contact Person
                      </h5>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>
                          Contact Person Name
                          <sup className="text-danger ms-2 fw-bolder">*</sup>
                        </label>
                        <input
                          readOnly={isViewing}
                          name="contact_person_name"
                          className="form-control"
                          value={formData.contact_person_name}
                          onChange={handleInputChange}
                          required
                          onInvalid={(e) =>
                            e.target.setCustomValidity(
                              "Please enter the contact person name",
                            )
                          }
                          onInput={(e) => e.target.setCustomValidity("")} // clears error once user types
                        />
                      </div>

                      <div className="col-md-4">
                        <label>Phone</label>
                        <input
                          readOnly={isViewing}
                          name="contact_person_phone"
                          className="form-control"
                          value={formData.contact_person_phone}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Mobile</label>
                        <input
                          readOnly={isViewing}
                          name="contact_person_mobile"
                          className="form-control"
                          value={formData.contact_person_mobile}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>Email</label>
                        <input
                          readOnly={isViewing}
                          name="contact_person_email"
                          type="email"
                          className="form-control"
                          value={formData.contact_person_email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Designation</label>
                        <input
                          readOnly={isViewing}
                          name="contact_person_designation"
                          className="form-control"
                          value={formData.contact_person_designation}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Alternate Contact Person */}
                  <div>
                    <div className="section-header d-flex align-items-center mb-3">
                      <div className="section-badge me-2">3</div>
                      <h5 className="section-title mb-0">
                        Alternate Contact Person
                      </h5>
                    </div>

                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>
                          Contact Person Name
                          {/* <sup className="text-danger ms-2 fw-bolder">*</sup> */}
                        </label>
                        <input
                          readOnly={isViewing}
                          name="alt_contact_person_name"
                          className="form-control"
                          value={formData.alt_contact_person_name}
                          onChange={handleInputChange}
                          // required
                          // onInvalid={(e) =>
                          //   e.target.setCustomValidity(
                          //     "Please enter the contact person name"
                          //   )
                          // }
                          // onInput={(e) => e.target.setCustomValidity("")}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Phone</label>
                        <input
                          readOnly={isViewing}
                          name="alt_contact_person_phone"
                          className="form-control"
                          value={formData.alt_contact_person_phone}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Mobile</label>
                        <input
                          readOnly={isViewing}
                          name="alt_contact_person_mobile"
                          className="form-control"
                          value={formData.alt_contact_person_mobile}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-4">
                        <label>Email</label>
                        <input
                          readOnly={isViewing}
                          name="alt_contact_person_email"
                          type="email"
                          className="form-control"
                          value={formData.alt_contact_person_email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label>Designation</label>
                        <input
                          readOnly={isViewing}
                          name="alt_contact_person_designation"
                          className="form-control"
                          value={formData.alt_contact_person_designation}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  {isViewing ? (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="cancle-btn"
                        onClick={closeModal}
                        disabled={isSubmitting} // disable cancel while submitting (optional)
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="custom-btn"
                        disabled={isSubmitting} // prevent double submits
                      >
                        {isSubmitting
                          ? isEditing
                            ? "Updating..."
                            : "Submitting..."
                          : isEditing
                            ? "Update"
                            : "Submit"}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showStatusModal && selectedEnterprise && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-history modal-dialog-centered history-model">
            <div className="modal-content position-relative">
              {/* Close Button */}
              <button
                type="button"
                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                disabled={isChangingStatus}
                onClick={() => {
                  if (!isChangingStatus) {
                    setShowStatusModal(false);
                    setSelectedEnterprise(null);
                  }
                }}
              >
                <img
                  src={crossIcon}
                  className="icon-image cross-i"
                  alt="Close"
                />
              </button>

              {/* Modal Body */}
              <div className="text-center p-3">
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <img
                    src={changeStatusIcon}
                    alt="Change Status"
                    style={{ width: "35px", height: "35px" }}
                  />
                </div>

                <p className="fw-bold mt-3 mb-3 refresh-prompts ak-text-md">
                  Are you sure you want to{" "}
                  <span className="text-black fw-bold ak-text-md">
                    {selectedEnterprise.status === "1"
                      ? "Deactivate"
                      : "Activate"}
                  </span>{" "}
                  this enterprize?
                </p>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer justify-content-center gap-2">
                <button
                  type="button"
                  disabled={isChangingStatus}
                  className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedEnterprise(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={isChangingStatus}
                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                  onClick={() =>
                    handleChangeStatus(
                      selectedEnterprise.id,
                      selectedEnterprise.status,
                    )
                  }
                >
                  {isChangingStatus ? "Updating..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enterprize;
